/**
 * Per-developer favourited feature flags for the dev-tools panel.
 *
 * The atom holds a Map of `${provider}:${flagKey}` to `true`. Favourites
 * surface flags into a pinned "Favourites" group at the top of the Flags
 * tab, independent of any tab-scoped overrides — favouriting a flag does
 * not change its evaluated value.
 *
 * Persistence: backed by `localStorage` (unlike `flagOverridesAtom`,
 * which uses sessionStorage). Favourites are a long-lived developer
 * preference that should survive tab close and apply across tabs, so
 * localStorage is the right fit. The "forgotten override follows me
 * forever" footgun that pushed overrides to sessionStorage doesn't
 * apply here — a starred flag is inert until you toggle it.
 */
import { getDefaultStore } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import type { FlagProvider } from './overrides';

/** Map key shape: `${provider}:${flagKey}`. */
export type FlagFavouritesMap = Readonly<Record<string, true>>;

const EMPTY_FAVOURITES: FlagFavouritesMap = Object.freeze({});

export const FLAG_FAVOURITES_STORAGE_KEY = 'heidi.devTools.flagFavourites';

const favouriteKey = (provider: FlagProvider, flagKey: string) =>
  `${provider}:${flagKey}`;

/**
 * SSR-safe localStorage adapter. Atom is read on the server during
 * Next.js page rendering, so we have to no-op when `window` is absent.
 */
const localStorageAdapter = createJSONStorage<FlagFavouritesMap>(() => {
  if (typeof window === 'undefined') {
    return {
      getItem: () => null,
      setItem: () => undefined,
      removeItem: () => undefined,
    };
  }
  return window.localStorage;
});

/**
 * Favourited flags, persisted to localStorage. Cross-tab and survives
 * tab close — a deliberate departure from `flagOverridesAtom` since
 * favourites are inert preference data, not session state.
 */
export const favouritesAtom = atomWithStorage<FlagFavouritesMap>(
  FLAG_FAVOURITES_STORAGE_KEY,
  EMPTY_FAVOURITES,
  localStorageAdapter,
  { getOnInit: true }
);

favouritesAtom.debugLabel = 'favouritesAtom (dev-tools)';

/**
 * Read whether a flag is favourited. Safe to call from non-React
 * contexts (uses Jotai's default store).
 */
export const isFavourite = (provider: FlagProvider, flagKey: string): boolean => {
  const favourites = getDefaultStore().get(favouritesAtom);
  return favourites[favouriteKey(provider, flagKey)] === true;
};

/** Explicitly set or clear a single favourite. */
export const setFavourite = (
  provider: FlagProvider,
  flagKey: string,
  value: boolean
): void => {
  const store = getDefaultStore();
  const current = store.get(favouritesAtom);
  const key = favouriteKey(provider, flagKey);
  const existing = current[key] === true;

  if (value === existing) return;

  const next: Record<string, true> = { ...current };
  if (value) {
    next[key] = true;
  } else {
    delete next[key];
  }
  store.set(favouritesAtom, Object.keys(next).length === 0 ? EMPTY_FAVOURITES : next);
};

/** Flip the favourite state for a flag. Returns the new state. */
export const toggleFavourite = (provider: FlagProvider, flagKey: string): boolean => {
  const next = !isFavourite(provider, flagKey);
  setFavourite(provider, flagKey, next);
  return next;
};

/** Clear every favourite across all providers. */
export const clearAllFavourites = (): void => {
  const store = getDefaultStore();
  if (Object.keys(store.get(favouritesAtom)).length === 0) return;
  store.set(favouritesAtom, EMPTY_FAVOURITES);
};
